import { Banner, BlockStack, Form, FormLayout, InlineStack, Modal, Spinner, Text, TextField } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import helpers from 'helpers/index';
import { TypedUser } from 'interface/user.model';
import { useMutateCreateCustomer } from 'queries/customer.query';
import { useCreateUserSocialProfile } from 'queries/user_social_profile.query';
import { useMutateSearchUser } from 'queries/users.query';

type CustomerSocialAssignedModalProps = {
  show: boolean;
  socialName: string;
  socialSlug: string;
  onClose: (assigned?: boolean) => void;
};

function takeNumberOnly(input: string): string {
  return input.replace(/\D/g, '');
}

export default function CustomerSocialAssignedModal({ show, socialName, socialSlug, onClose }: CustomerSocialAssignedModalProps) {
  const { mutateAsync: searchUser, isPending: isSearching } = useMutateSearchUser();
  const { mutateAsync: createCustomer, isPending: isCreating } = useMutateCreateCustomer();
  const { mutateAsync: createSocialProfile, isPending: isAssigning } = useCreateUserSocialProfile();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [matchedCustomer, setMatchedCustomer] = useState<TypedUser | null>(null);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const searchSequence = useRef(0);

  const resetForm = useCallback(() => {
    searchSequence.current += 1;
    setPhoneNumber('');
    setCustomerName('');
    setMatchedCustomer(null);
    setSearchCompleted(false);
    setErrorMessage('');
  }, []);

  useEffect(() => {
    if (!show) resetForm();
  }, [resetForm, show]);

  const loadCustomer = useCallback(
    async (phone: string, sequence: number) => {
      try {
        const customers = await searchUser({ user_phonenumber: phone });
        if (sequence !== searchSequence.current) return;

        const customer = customers.find((item) => takeNumberOnly(item.user_phonenumber || '') === phone) || null;
        setMatchedCustomer(customer);
        setCustomerName(customer?.display_name || '');
        setSearchCompleted(true);
      } catch (error: any) {
        if (sequence !== searchSequence.current) return;
        setMatchedCustomer(null);
        setSearchCompleted(false);
        setErrorMessage(error?.response?.data?.message || error?.message || 'Không thể tìm khách hàng.');
      }
    },
    [searchUser]
  );

  const searchCustomerDebounce = useMemo(
    () =>
      helpers.debounce((phone: string, sequence: number) => {
        loadCustomer(phone, sequence);
      }, 400),
    [loadCustomer]
  );

  const handlePhoneChange = useCallback(
    (value: string) => {
      const phone = takeNumberOnly(value);
      const sequence = ++searchSequence.current;
      setPhoneNumber(phone);
      setMatchedCustomer(null);
      setCustomerName('');
      setSearchCompleted(false);
      setErrorMessage('');

      if (helpers.isPhoneNumber(phone)) searchCustomerDebounce(phone, sequence);
    },
    [searchCustomerDebounce]
  );

  const handlePaste = useCallback(async () => {
    try {
      handlePhoneChange(await navigator.clipboard.readText());
    } catch (error) {}
  }, [handlePhoneChange]);

  const phoneError = phoneNumber && !helpers.isPhoneNumber(phoneNumber) ? 'Số điện thoại không hợp lệ.' : undefined;
  const isSubmitting = isCreating || isAssigning;
  const canSubmit =
    helpers.isPhoneNumber(phoneNumber) &&
    searchCompleted &&
    Boolean(matchedCustomer?.user_id || customerName.trim()) &&
    Boolean(socialName && socialSlug) &&
    !isSearching &&
    !isSubmitting;

  let customerStatus: { text: string; tone: 'subdued' | 'success' } = {
    text: 'Nhập số điện thoại để tìm khách hàng.',
    tone: 'subdued',
  };
  if (isSearching) customerStatus = { text: 'Đang tìm khách hàng...', tone: 'subdued' };
  else if (matchedCustomer) customerStatus = { text: 'Đã tìm thấy khách hàng.', tone: 'success' };
  else if (searchCompleted) customerStatus = { text: 'Khách hàng mới — nhập tên để tạo.', tone: 'subdued' };

  const handleAssign = useCallback(async () => {
    if (!canSubmit) return;

    try {
      setErrorMessage('');
      let userId = matchedCustomer?.user_id;

      if (!userId) {
        const customer = await createCustomer({
          display_name: customerName.trim(),
          user_phonenumber: phoneNumber,
        });
        userId = customer?.user_id;
      }

      if (!userId) throw new Error('Không lấy được ID khách hàng để gán tài khoản Zalo.');

      await createSocialProfile({
        user_id: userId,
        social_name: socialName,
        social_slug: socialSlug,
      });
      onClose(true);
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || error?.message || 'Không thể gán tài khoản Zalo cho khách hàng.');
    }
  }, [canSubmit, createCustomer, createSocialProfile, customerName, matchedCustomer?.user_id, onClose, phoneNumber, socialName, socialSlug]);

  return (
    <Modal
      open={show}
      onClose={() => onClose(false)}
      title="Gán cuộc gọi Zalo vào khách hàng"
      primaryAction={{
        content: matchedCustomer ? 'Gán vào khách hàng' : 'Tạo khách và gán',
        loading: isSubmitting,
        disabled: !canSubmit,
        onAction: handleAssign,
      }}
      secondaryActions={[{ content: 'Đóng', disabled: isSubmitting, onAction: () => onClose(false) }]}
    >
      <Modal.Section>
        <Form onSubmit={handleAssign}>
          <BlockStack gap="400">
            {errorMessage ? (
              <Banner tone="critical" onDismiss={() => setErrorMessage('')}>
                <Text as="p">{errorMessage}</Text>
              </Banner>
            ) : null}

            <BlockStack gap="100">
              <Text as="p" tone="subdued">
                Tài khoản Zalo cần gán
              </Text>
              <Text as="p" fontWeight="semibold">
                {socialSlug}
              </Text>
            </BlockStack>

            <FormLayout>
              <TextField
                label="SĐT"
                placeholder="Nhập số điện thoại"
                autoComplete="off"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                value={phoneNumber}
                onChange={handlePhoneChange}
                error={phoneError}
                disabled={isSubmitting}
                clearButton
                onClearButtonClick={() => handlePhoneChange('')}
                labelAction={{ content: 'Dán', onAction: handlePaste }}
              />

              <TextField
                label="Tên khách hàng"
                placeholder={searchCompleted && !matchedCustomer ? 'Nhập tên để tạo khách hàng mới' : 'Tên tự động hiển thị sau khi tìm SĐT'}
                autoComplete="off"
                value={customerName}
                onChange={(value) => setCustomerName(helpers.capitalizeWords(value))}
                disabled={Boolean(matchedCustomer) || isSubmitting || !searchCompleted}
              />
            </FormLayout>

            <div style={{ minHeight: 20 }}>
              <InlineStack gap="200" blockAlign="center">
                {/* {isSearching ? <Spinner size="small" /> : null} */}
                <Text as="p" tone={customerStatus.tone}>
                  {customerStatus.text}
                </Text>
              </InlineStack>
            </div>
          </BlockStack>
        </Form>
      </Modal.Section>
    </Modal>
  );
}
